import React from "react";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Box,
  Button,
  Flex,
  Progress,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import NumberField from "../NumberField/NumberField";
import { InputFilter } from "./components";
import { Paginator } from "../Paginator";
import { showErrorToast, SORT_DIRECTIONS } from "../../utils";
import { utils, writeFile } from "xlsx";
import { useAuth } from "../../context";
import { cellStyle } from "./cellStyle";

/**
 * Currently this is near identical to DateTableV2. We will replace this with DataTableV2 soon.
 * @deprecated Use DataTableV2 instead
 */
function DataTable({
  data,
  columns,
  isLoading,
  serverSidePagination = false,
  queryPageCount,
  queryTotalCount,
  queryPageIndex = 0,
  setQueryPageIndex,
  queryPageSize = 20,
  setQueryPageSize,
  queryPageSortBy = [],
  setQueryPageSortBy,
  filterObj = {},
  setFilterObj,
  containerHeight,
  enableFilters = false,
  enableSorting = false,
  showPaginator = true,
  downloadButtonText = "Export",
  showDownloadButton = true,
  // Optional. For server-paginated tables, `data` holds only the page on screen, so Export would write only that
  // page. Pass an async function resolving to EVERY row (plain objects keyed by column id) and Export writes those
  // instead, with readable headers (`meta.exportHeader`, else a string `header`, else the column id).
  fetchAllRowsForExport,
  // Optional, with `fetchAllRowsForExport`: the file's columns as `[{ id, header }]`, when they should differ from
  // the screen's (e.g. an amount shown as "£1,200" exported as a number beside its currency).
  exportColumns,
  fontSize = "body.m",
  ...rest
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataTable",
  });

  const { user } = useAuth();
  const toast = useToast();
  const [isExporting, setIsExporting] = React.useState(false);

  const defaultColumn = React.useMemo(
    () => ({
      Filter: (data) => <InputFilter column={data.column} filterObj={filterObj} setFilterObj={setFilterObj} />,
    }),
    [filterObj, setFilterObj]
  );

  const table = useReactTable({
    data,
    columns,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    initialState: {
      pagination: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
    },
    pageCount: queryPageCount,

    manualSorting: serverSidePagination,
    manualFiltering: serverSidePagination,
    manualPagination: serverSidePagination,

    autoResetSortBy: false,
    autoResetFilters: false,

    enableFilters: enableFilters,
    enableSorting: enableSorting,
    defaultColumn: defaultColumn,
  });

  const headerBg = useColorModeValue("white", "gray.800");
  const headerShadowColor = useColorModeValue("gainsboro", "lightslategrey");

  /**
   * Exports TanStack Table data as JSON
   * @param {Object} table - The TanStack Table instance
   * @param {Object} options - Export options
   * @returns {Array} Array of JSON objects representing the table data
   */
  function exportTableAsJson(table, options = {}) {
    const { includePagination = false, processedValues = true, visibleColumnsOnly = true } = options;

    // Get the rows to export
    const rowsToExport = includePagination ? table.getRowModel().rows : table.getCoreRowModel().rows;

    // Get columns
    const columns = visibleColumnsOnly ? table.getVisibleLeafColumns() : table.getAllLeafColumns();

    // Transform the data
    const exportData = rowsToExport.map((row) => {
      const exportRow = {};

      columns.forEach((column) => {
        const columnId = column.id;
        const columnDef = column.columnDef;
        let value;

        try {
          // Attempt to use the cell function if it exists
          if (typeof columnDef.cell === "function") {
            value = columnDef.cell({ row, column, table });
            if (typeof value === "object" && value !== null && value.props) {
              value = value.props.children;
            } else if (typeof value === "object") {
              value = value.getValue();
            }
          } else {
            throw new Error("Cell function not available");
          }
        } catch {
          try {
            // Attempt to use TanStack's getValue method
            value = row.getValue(columnId);
          } catch {
            try {
              // Fall back to direct access via accessorKey
              if (columnDef.accessorKey) {
                value = row.original[columnDef.accessorKey];
              } else {
                throw new Error("Accessor key not available");
              }
            } catch {
              // Default to null if all methods fail
              value = null;
            }
          }
        }

        // Add to export row
        exportRow[columnId] = value;
      });

      return exportRow;
    });

    return exportData;
  }

  const writeWorkbook = (ws) => {
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");
    writeFile(wb, "export.xlsx");
  };

  const exportHeaderOf = (column) => {
    const { meta, header } = column.columnDef;
    if (meta?.exportHeader) return meta.exportHeader;
    return typeof header === "string" ? header : column.id;
  };

  const downloadAllRows = async () => {
    setIsExporting(true);
    try {
      const rows = await fetchAllRowsForExport();
      const fileColumns =
        exportColumns ??
        table.getVisibleLeafColumns().map((column) => ({ id: column.id, header: exportHeaderOf(column) }));
      const ids = fileColumns.map((column) => column.id);
      const ws = utils.json_to_sheet(
        rows.map((row) => Object.fromEntries(ids.map((id) => [id, row[id] ?? null]))),
        { header: ids }
      );
      utils.sheet_add_aoa(ws, [fileColumns.map((column) => column.header)], { origin: "A1" });
      writeWorkbook(ws);
    } catch (err) {
      showErrorToast(toast, err?.response?.data?.parameters?.message ?? err?.message ?? String(err));
    } finally {
      setIsExporting(false);
    }
  };

  const downloadExcel = () => {
    if (fetchAllRowsForExport) {
      downloadAllRows();
      return;
    }
    const exportedData = exportTableAsJson(table);
    writeWorkbook(utils.json_to_sheet(exportedData));
  };

  return (
    <Box>
      {showDownloadButton && (
        <Flex justifyContent="flex-end">
          <Button colorScheme="customScheme" m={2} isLoading={isExporting} onClick={() => downloadExcel()}>
            {downloadButtonText}
          </Button>
        </Flex>
      )}
      <Box
        pos="relative"
        borderWidth="thin"
        borderRadius="md"
        overflow="hidden"
        backgroundColor="white"
        {...rest}
      >
        {isLoading && (
          <Progress
            size="xs"
            isIndeterminate
            pos="absolute"
            insetInline="0"
            insetBlockStart="0"
            zIndex="3"
            colorScheme="customScheme"
          />
        )}
        <TableContainer overflowX="auto" overflowY="visible">
          <Table>
            <Thead>
              {table
                .getHeaderGroups()
                .map((headerGroup) =>
                  renderHeader(
                    headerGroup,
                    headerBg,
                    headerShadowColor,
                    setQueryPageSortBy,
                    filterObj,
                    setFilterObj,
                    serverSidePagination
                  )
                )}
            </Thead>
            <Tbody fontSize={fontSize}>
              {table.getFilteredRowModel().rows.length === 0 && (
                <Tr>
                  <Td colSpan={999} textAlign="center">
                    {t("noDataMessage")}
                  </Td>
                </Tr>
              )}
              {table.getRowModel().rows.map((row) => renderRow(row))}
            </Tbody>
          </Table>
        </TableContainer>
        {table.getFilteredRowModel().rows.length > 0 && showPaginator && (
          <Box m={4}>
            <Paginator
              selectedPage={table.getState().pagination.pageIndex + 1}
              numberOfPages={Math.max(1, table.getPageCount())}
              handlePageSelection={(page) => {
                const idx = page - 1;
                table.setPageIndex(idx);
                if (setQueryPageIndex) setQueryPageIndex(idx);
                if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
              }}
              pageSize={table.getState().pagination.pageSize}
              onPageSizeChange={(size) => {
                table.setPageSize(size);
                if (setQueryPageSize) setQueryPageSize(size);
              }}
              totalCount={serverSidePagination ? queryTotalCount : table.getFilteredRowModel().rows.length}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const renderRow = (row) => {
  const rowCustomStyles = row.original.rowCustomStyles ?? {};

  return (
    <Tr key={row.id} _hover={{ bg: "gray.50" }} transition="background 0.1s" {...rowCustomStyles}>
      {row.getVisibleCells().map((cell) => {
        const isNumeric = !isNaN(cell.getValue());
        if (cell.getValue() === null || typeof cell.getValue() === "undefined") {
          cell.value = "";
        }

        const styleObj = cellStyle(cell);

        return (
          <Td key={cell.id} whiteSpace="pre-wrap" style={styleObj} py={4} borderColor="gray.100" color="gray.700" fontSize="14px">
            {isNumeric && !cell?.column?.columnDef?.skipNumericCheck ? (
              <NumberField value={cell.getValue()} align={"left"} />
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </Td>
        );
      })}
    </Tr>
  );
};

const handleColumnHeaderClick = async (column, setQueryPageSortBy) => {
  let newSortDirection;
  switch (column.columnDef.sortDirection) {
    case SORT_DIRECTIONS.NONE:
      newSortDirection = SORT_DIRECTIONS.ASC;
      break;
    case SORT_DIRECTIONS.ASC:
      newSortDirection = SORT_DIRECTIONS.DESC;
      break;
    case SORT_DIRECTIONS.DESC:
      newSortDirection = SORT_DIRECTIONS.NONE;
      break;
    default:
      newSortDirection = SORT_DIRECTIONS.NONE;
  }

  if (setQueryPageSortBy) {
    setQueryPageSortBy([`${column.id},${newSortDirection}`]);
  }
};

function renderHeader(headerGroup, bg, shadowColor, setQueryPageSortBy, filterObj, setFilterObj, serverSideSorting) {
  return (
    <Tr key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        const column = header.column;
        const styleObj = {
          verticalAlign: "top",
          paddingInline: "var(--chakra-space-4)",
        };

        if (header.getSize()) {
          styleObj["minWidth"] = header.getSize();
          styleObj["maxWidth"] = header.getSize();
        }

        if (column.columnDef.customStyles && Object.keys(column.columnDef.customStyles)?.length > 0) {
          Object.keys(column.columnDef.customStyles).forEach((attr) => {
            styleObj[attr] = column.columnDef.customStyles[attr];
          });
        }

        return (
          <Th
            key={header.id}
            style={styleObj}
            pos="sticky"
            insetBlockStart="0"
            zIndex={2}
            bg="gray.50"
            borderBottom="1px solid"
            borderColor="gray.200"
            boxShadow="none"
            userSelect="none"
            color="gray.500"
            fontSize="13px"
            fontWeight="600"
            textTransform="none"
            letterSpacing="normal"
            py={3}
            cursor="default !important"
          >
            {column.getCanSort() &&
            (typeof column.columnDef.isSortable === "undefined" || column.columnDef.isSortable) ? (
              <Flex
                align="center"
                onClick={
                  serverSideSorting
                    ? () => handleColumnHeaderClick(column, setQueryPageSortBy)
                    : header.column.getToggleSortingHandler()
                }
                cursor="pointer"
              >
                <Box marginRight={1}>{flexRender(column.columnDef.header, header.getContext())}</Box>
                <Box>
                  {column.columnDef.sortDirection === SORT_DIRECTIONS.ASC || column.getIsSorted() === "asc" ? (
                    <FaSortUp />
                  ) : column.columnDef.sortDirection === SORT_DIRECTIONS.DESC || column.getIsSorted() === "desc" ? (
                    <FaSortDown />
                  ) : (
                    <FaSort />
                  )}
                </Box>
              </Flex>
            ) : (
              <Box>{flexRender(column.columnDef.header, header.getContext())}</Box>
            )}
            {column.getCanFilter()
              ? (column.columnDef?.filter?.component?.(column, column.columnDef?.filter?.params) ?? (
                  <InputFilter column={column} filterObj={filterObj} setFilterObj={setFilterObj} />
                ))
              : null}
          </Th>
        );
      })}
    </Tr>
  );
}

export default DataTable;
